import { useState } from "react";
import { CheckCircle2, Loader2, Shield, User, Phone, Mail, UserCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Consent gate on /pay-now. Payment details are only revealed after the
 * parent enters name/phone (email+student name optional) and ticks the
 * terms-acknowledgement checkbox.
 *
 * Backend stores an immutable record: {id, name, phone, email, student_name,
 * terms_version, accepted_at (UTC server time), ip, user_agent}.
 */
export default function PaymentConsentGate({ onAccepted }) {
    const [form, setForm] = useState({ name: "", phone: "", email: "", student_name: "" });
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error("Name and phone are required.");
            return;
        }
        if (!agreed) {
            toast.error("Please tick the agreement to proceed.");
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.post("/payment-consent", {
                name: form.name.trim(),
                phone: form.phone.trim(),
                email: form.email.trim() || null,
                student_name: form.student_name.trim() || null,
                agreed: true,
            });
            toast.success("Consent recorded. Payment details are now visible below.");
            onAccepted?.(r.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not record consent. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="py-10 bg-[#F8FAFC] border-b border-slate-200" data-testid="payment-consent-gate">
            <div className="max-w-3xl mx-auto px-6 lg:px-8">
                <div className="bg-white border-2 border-[#1E3A8A]/15 rounded-2xl p-6 sm:p-8 shadow-lg">
                    <div className="flex items-start gap-3 mb-5">
                        <div className="w-10 h-10 rounded-xl bg-[#1E3A8A] text-white flex items-center justify-center shrink-0">
                            <Shield className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#3B82F6] mb-1">Step 1 of 2 — Compliance</div>
                            <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#0F172A]">Acknowledge Parent Terms before payment</h3>
                            <p className="text-sm text-slate-500 mt-1">Please enter your details and confirm you've read the 9-clause Parent Terms above. Payment details will be revealed once consent is recorded.</p>
                        </div>
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-semibold text-slate-600 mb-1 block flex items-center gap-1.5">
                                    <User className="w-3.5 h-3.5" /> Parent Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text" required value={form.name} onChange={setF("name")}
                                    data-testid="consent-name"
                                    placeholder="Your full name"
                                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-600 mb-1 block flex items-center gap-1.5">
                                    <Phone className="w-3.5 h-3.5" /> Phone / WhatsApp <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="tel" required value={form.phone} onChange={setF("phone")}
                                    data-testid="consent-phone"
                                    placeholder="+91 98765 43210"
                                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-600 mb-1 block flex items-center gap-1.5">
                                    <Mail className="w-3.5 h-3.5" /> Email <span className="text-slate-400 font-normal">(optional)</span>
                                </label>
                                <input
                                    type="email" value={form.email} onChange={setF("email")}
                                    data-testid="consent-email"
                                    placeholder="you@example.com"
                                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-600 mb-1 block flex items-center gap-1.5">
                                    <UserCircle2 className="w-3.5 h-3.5" /> Student Name <span className="text-slate-400 font-normal">(optional)</span>
                                </label>
                                <input
                                    type="text" value={form.student_name} onChange={setF("student_name")}
                                    data-testid="consent-student"
                                    placeholder="For our records"
                                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                                />
                            </div>
                        </div>

                        <label className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl cursor-pointer hover:bg-amber-100/60 transition-colors">
                            <input
                                type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                                data-testid="consent-agree"
                                className="mt-0.5 w-4 h-4 shrink-0 accent-[#1E3A8A]"
                            />
                            <span className="text-sm text-slate-800 leading-relaxed">
                                <strong>I have read and agree</strong> to the <a href="/policies/parent" target="_blank" rel="noreferrer" className="text-[#1E3A8A] underline font-semibold">Parent Policy (9 Terms)</a>, <a href="/policies/terms" target="_blank" rel="noreferrer" className="text-[#1E3A8A] underline font-semibold">Terms &amp; Conditions</a> and <a href="/policies/refund" target="_blank" rel="noreferrer" className="text-[#1E3A8A] underline font-semibold">Refund Policy</a> of Anand Tutorials. I understand that all payments must flow only through official Anand Tutorials channels, and that direct dealings with tutors are strictly prohibited.
                            </span>
                        </label>

                        <button
                            type="submit" disabled={submitting || !agreed}
                            data-testid="consent-submit"
                            className="w-full inline-flex items-center justify-center gap-2 bg-[#1E3A8A] hover:bg-[#1E40AF] disabled:bg-slate-300 disabled:text-slate-500 text-white font-heading font-bold px-6 py-3.5 rounded-xl transition-colors"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                            {submitting ? "Recording consent…" : "I Agree — Show Payment Details"}
                        </button>
                        <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                            Your acceptance is timestamped and stored for compliance. This creates a legal audit trail protecting both you and Anand Tutorials.
                        </p>
                    </form>
                </div>
            </div>
        </section>
    );
}
